import { cn } from "@mabi-ui/utils";
import { Button, Tooltip, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { Folder } from "certiblok-api-manager";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnapshot } from "valtio";
import { watch } from "valtio/utils";
import BetterCheckbox from "../../../../../../../utils/components/BetterCheckbox";
import DocumentsCount from "../../../../../../../utils/components/FolderComponents/DocumentsCount";
import FolderIcon from "../../../../../../../utils/components/FolderComponents/FolderIcon";
import SubfoldersCount from "../../../../../../../utils/components/FolderComponents/SubfoldersCount";
import CertiblokIcon from "../../../../../../../utils/components/icons/CertiblokIcon";
import WhitePopperTooltip from "../../../../../../../utils/components/poppers/WhitePopperTooltip";
import { useTreeViewDocumentPickerContext } from "../../../../../../../utils/components/TreeViewDocumentPicker/controllers/TreeViewDocumentPickerContext";
import { getFolderDocuments } from "../../../../../../../utils/controllers/TreeViewController/functions/treeViewFunctions";
import {
	useFolderStatus,
	useToggleOpenFolder,
} from "../../../../../../../utils/controllers/TreeViewController/hooks/treeViewFolderHooks";
import { useTreeViewContext } from "../../../../../../../utils/controllers/TreeViewController/TreeViewController";
import { TreeViewListItem } from "../../../../../../../utils/controllers/TreeViewController/types/ListItem";
import { useBetterMediaQuery } from "../../../../../../../utils/hooks/useBetterMediaQuery";
import { CertiblokDocument } from "../../../../../../../utils/interfaces/Document";

export function FolderItem({ item, canSelect = false }: { item: TreeViewListItem; canSelect?: boolean }) {
	const itemSnap = useSnapshot(item);
	const { selectedDocuments, setSelectedDocuments } = useTreeViewDocumentPickerContext();
	const toggleFolderOpen = useToggleOpenFolder();
	const folderStatus = useFolderStatus(item.id!);
	const { itemsList } = useTreeViewContext();
	const isMd = useBetterMediaQuery("md");
	const isSm = useBetterMediaQuery("sm");
	const [isSelected, setIsSelected] = useState(false);
	const { t } = useTranslation();

	useEffect(() => {
		const unsub = watch((get) => {
			const items = get(itemsList);
			const selectedItems = get(selectedDocuments);
			if (!itemSnap.item?.id) return;
			const documents = getFolderDocuments(itemSnap.item?.id, items);
			if (documents.length > 0) {
				setIsSelected(
					documents.every((document) => document.item && !document.item.isFolder && selectedItems.has(document.item.id))
				);
			}
		});
		return () => unsub();
	}, [selectedDocuments, itemSnap.item?.id]);

	if (!itemSnap.item?.isFolder) return null;

	const folder = itemSnap.item as unknown as Folder;
	const isSelectionDisabled = !folderStatus?.isExpanded && !isSelected;

	const requiredSpace = Math.max(0, (itemSnap.parentFolder.deepness || 0) + (canSelect ? 1 : 0));

	return (
		<div className={clsx("justify-start text-black !tracking-[0.25px] w-full h-full flex items-center rounded-xl px-1")}>
			<Box
				className="grid items-center gap-2 md:gap-4 w-full"
				sx={{
					gridTemplateColumns: {
						xs: `1fr `,
						sm: `3fr 1fr 1fr `,
					},
				}}
			>
				<Box
					className="grid items-center gap-4 w-full"
					sx={{
						gridTemplateColumns: {
							xs: `${itemSnap.parentFolder.deepness || canSelect ? "min-content" : ""} 1fr`,
							md: `repeat(${requiredSpace}, min-content) 1fr`,
						},
					}}
				>
					{isMd ? (
						Array.from(Array(itemSnap.parentFolder.deepness)).map((_, index) => {
							return <div className="w-[20px] h-[20px]" key={index} />;
						})
					) : itemSnap.parentFolder.deepness > 0 && !canSelect ? (
						<div className="w-[20px] h-[20px]" />
					) : null}

					{canSelect && (
						<WhitePopperTooltip
							enabled={isSelectionDisabled}
							title={t("treeViewDocumentPicker.folder.disabledSelectionTitle")}
						>
							<BetterCheckbox
								color="secondary"
								className={cn("p-1", isSelectionDisabled ? "opacity-50" : "cursor-pointer")}
								checked={isSelected}
								disabled={isSelectionDisabled}
								onClick={(ev) => {
									ev.stopPropagation();
									ev.preventDefault();
									if (isSelectionDisabled) return;
									if (itemSnap.item?.id) {
										const documents = getFolderDocuments(itemSnap.item?.id, itemsList);
										if (isSelected) {
											setSelectedDocuments(
												[...Array.from(selectedDocuments.values())].filter((document) => {
													return documents.findIndex((doc) => doc.item?.id === document.id) === -1;
												})
											);
										} else {
											setSelectedDocuments(
												documents.reduce(
													(acc, document) => {
														if (document.item && !selectedDocuments.has(document.item.id) && !document.item.isFolder) {
															acc.push(document.item as unknown as CertiblokDocument);
														}
														return acc;
													},
													[...Array.from(selectedDocuments.values())]
												)
											);
										}
									}
								}}
							/>
						</WhitePopperTooltip>
					)}
					<Button
						color="secondary"
						onClick={(ev) => {
							toggleFolderOpen({
								folder: { folderId: item.id, childrenCounts: folder.totalRootChildren ?? 0 },
								parentFolder: itemSnap.parentFolder,
							});
						}}
						className="w-full hover:tracking-[0.25px] gap-4 p-1 items-center box-border h-[32px]"
					>
						<CertiblokIcon
							size={20}
							color="black"
							name={"arrow_mini_outline_right"}
							className={clsx(folderStatus?.isExpanded ? "rotate-90" : "rotate-0", "transition-transform")}
						/>

						<div className="flex items-center gap-4 max-w-full w-full overflow-hidden">
							<FolderIcon folder={folder} disableReservationIcon={true} />
							<Tooltip title={folder.name} enterDelay={500}>
								<Typography variant="body1" noWrap className="w-full text-start text-black">
									{folder.name}
								</Typography>
							</Tooltip>
						</div>
					</Button>
				</Box>
				{isSm && <SubfoldersCount folder={folder} />}
				{isSm && <DocumentsCount folder={folder} />}
			</Box>
		</div>
	);
}
